import { Component, DestroyRef, forwardRef, inject, OnInit, signal } from '@angular/core';
import { Status } from '../../models/status.model';
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { StatusesService } from '../../services/statuses.service';
import { InvoiceService } from '../invoice.service';
import { ActivatedRoute } from '@angular/router';
import { Invoice } from '../../models/invoice.model';
import { ConfirmDialogComponent } from '../../main/confirm-dialog/confirm-dialog.component';
import { catchError, EMPTY, Observable, of } from 'rxjs';
import { toUnderscore } from '../../utils/string-utils';
import { HttpResponse } from '@angular/common/http';
import { AdminService } from '../../admin/services/admin.service';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { ChipComponent } from '../../shared/ui/chip/chip.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { HumanPipe } from '../../shared/pipes/human.pipe';
import { StatusPipe } from '../../shared/pipes/status.pipe';
import { GoToDetailsDirective } from '../../shared/directives/go-to-details.directive';
import { DocumentWorksComponent } from '../../shared/works/document-works.component';
import { MoneyPipe } from '../../shared/pipes/money.pipe';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FirebaseService } from '../../services/firebase.service';
import { RoleService } from '../../role/role.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SafePipe } from '../../shared/pipes/safe.pipe';
import moment from 'moment';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-invoice-details',
  templateUrl: './invoice-details.component.html',
  styleUrl: './invoice-details.component.scss',
  imports: [
    AsyncPipe,
    ChipComponent,
    forwardRef(() => GoToDetailsDirective),
    DatePipe,
    HumanPipe,
    MatButtonModule,
    MatDialogClose,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatToolbarModule,
    MatTooltipModule,
    MoneyPipe,
    SafePipe,
    StatusPipe,
    forwardRef(() => DocumentWorksComponent),
  ]
})
export class InvoiceDetailsComponent implements OnInit {
  
  public invoice = signal<Invoice>(null);

  statuses = signal<Status[]>([]);

  isMobile = signal<boolean>(false);

  loading = signal<boolean>(false);

  loadingPdf = signal<boolean>(true);

  private id: number;

  private readonly adminService = inject(AdminService);

  taxValue = this.adminService.getTaxValue();

  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly breakpointObserver = inject(BreakpointObserver);

  protected readonly invoiceService = inject(InvoiceService);

  private readonly statusService = inject(StatusesService);

  private readonly dialog = inject(MatDialog);

  public readonly dialogRef = inject(MatDialogRef<InvoiceDetailsComponent>);

  public readonly data = inject(MAT_DIALOG_DATA);

  private readonly snackBar = inject(MatSnackBar);

  private readonly firebaseService = inject(FirebaseService);

  private readonly _destroyRef = inject(DestroyRef);

  private readonly roleService = inject(RoleService);

  
  get alreadyPayed(): boolean {
    return moment(this.invoice()?.due_date).isBefore(moment());
  }

  get isElectronicInvoice(): boolean {
    return this.invoice()?.invoice_type === 'electronic_invoice';
  }

  get isElectronicTicket(): boolean {
    return this.invoice()?.invoice_type === 'electronic_ticket';
  }

  get isCreditNote(): boolean {
    return this.invoice()?.invoice_type === 'credit_note';
  }

  get isDebitNote(): boolean {
    return this.invoice()?.invoice_type === 'debit_note';
  }

  get canUpdate(): Observable<boolean> {
    return this.roleService.can('update', 'invoice');
  }

  get canSendToValidation(): Observable<boolean> {
    if (!this.invoice()?.cancelled_at && !this.invoice()?.validated_at) {
      return this.canUpdate;
    } else {
      return of(false);
    }
  }

  get canCancel(): Observable<boolean> {
    if (this.isCreditNote && !this.invoice()?.cancelled_at) {
      return this.canUpdate;
    }

    return of(false);
  }

  get isSale(): boolean {
    return this.invoice()?.type === 'Invoice::Sale';
  }

  get Total(): number {
    let total = 0;
  
    this.invoice()?.data?.details?.forEach(product => {
      total += product.quantity * product.amount * (1 - product.discount_percent / 100);
    });
  
    return total;
  }

  ngOnInit(): void {
    if (this.data?.invoice) {
      this.invoice.set(this.data.invoice);
      this.id = this.invoice().id;
      this.openDetails(this.invoice().id);
    } else {
      this.activatedRoute.params.subscribe((params) => {
        if (params.id) {
          this.id = params.id;
          this.openDetails(params.id);
        }
      });
    }

    this.breakpointObserver.observe(['(max-width: 768px)']).subscribe((state: BreakpointState) => {
      this.isMobile.set(state.matches);
      if(Capacitor.isNativePlatform()){
        this.isMobile.set(true);
      }
    });

    this.firebaseService.subscribeToMessages(
      'validated-invoice',
      { id: this.id, type: 'Invoice::Sale' },
      this.handleValidatedDocumentMessage.bind(this),
      this._destroyRef
    );
  }

  handleValidatedDocumentMessage(message: any) {
    this.reloadInvoice();
    this.snackBar.open('El documento ha sido validado', 'Cerrar', { duration: 5000 });   
  }

  setStatus(status_id: number) {
    this.dialog
      .open(ConfirmDialogComponent, { data: { title: 'Actualizar Estado' } })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.invoice.update((prev) => ({...prev, status_id}));
          this.updateInvoice({ status_id });
        }
      });
  }

  openDetails(id: number) {
    this.loading.set(true);
    this.invoiceService.get(id).subscribe((response) => {
      this.loading.set(false);
      this.invoice.set(response.body);
      this.statusService.statuses('Status::' + this.invoice().type).subscribe((statuses) => {
        this.statuses.set(statuses);
      });
    });
  }

  updateInvoice(changes: Partial<Invoice>) {
    this.loading.set(true);
    this.invoiceService
      .update(this.invoice().id, changes).pipe(
        catchError(() => {
          this.loading.set(false);
          return EMPTY;
        }),
      )
      .subscribe((response: HttpResponse<Invoice>) => {
        this.loading.set(false);
        this.invoice.set(response.body)
        setTimeout(() => { this.updateWorks(); }, 1000);
      });
  }

  private updateWorks() {
    this.invoiceService.getWorks(this.invoice().id).pipe(
      catchError(() => {
        this.snackBar.open('Error al cargar los trabajos', 'Cerrar', { duration: 3000 });
        this.loading.set(false);
        return EMPTY;
      }),
    ).subscribe((response) => {
      this.invoice.update((prev) => ({ ...prev, works: response.body }));
    });
  }
  
  validateInvoice() {
    this.loading.set(true);
    this.invoiceService
      .validate(this.invoice().id).pipe(
        catchError((e) => {
          this.snackBar.open('Error al validar la factura', 'Cerrar', { duration: 3000 });
          this.loading.set(false);
          return EMPTY;
        }),
      ).subscribe((response: HttpResponse<Invoice>) => {
        this.loading.set(false);

        this.snackBar.open('Se ha enviado el documento al Sii para validación', 'Cerrar', { duration: 3000 });
      });
  }

  cancelInvoice() {
    this.dialog.open(
      ConfirmDialogComponent,
      {
        data: {
          title: '¿Anular nota de crédito?',
          body: `Esto creará una nota de débito la cual anulará la nota de crédito y no podrá ser revertida. ¿Está seguro?`,
        },
      },
    )
    .afterClosed().subscribe((response) => {
      if (!response) {
        return;
      }

      this.loading.set(true);
      const body = {
        type: 'Invoice::Sale',
        invoice_type: 'debit_note',
        document_id: this.invoice().id,
        document_type: 'Invoice',
        stakeholder_id: this.invoice().stakeholder.id,
        stakeholder_type: this.invoice().stakeholder.type,
        is_buy: false,
      }
      this.invoiceService.create(body).pipe(
        catchError(() => {
          this.snackBar.open('Error al crear la nota de débito', 'Cerrar', { duration: 3000 });
          this.loading.set(false);
          return EMPTY;
        }),
      ).subscribe(() => {
        this.snackBar.open('Se ha creado la nota de débito', 'Cerrar', { duration: 3000 });
        this.loading.set(false);
        this.dialogRef.close(true);
      });
    });
  }

  reloadInvoice() {
    this.loading.set(true);
    this.invoiceService.get(this.invoice().id).pipe(
      catchError(() => {
        this.loading.set(false);
        return EMPTY;
      }),
    ).subscribe((response) => {
      this.loading.set(false);
      this.invoice.set(response.body);
    });
  }

  pdfLoaded() {
    setTimeout(() => {
      this.loadingPdf.set(false); 
    }, 2900);
  }

  get title() {
    const action = this.adminService.getText(toUnderscore(this.invoice()?.invoice_type || this.invoice()?.type));
    const text = `${action} #${this.invoice()?.correlative_id}`;

    if (!this.invoice()?.folio_number) { return text; }

    return `${text} (Folio Nº ${this.invoice()?.folio_number})`;

  }
  
}
